import { Core } from "@faire/design-tokens";
import { useChatAuthorization } from "@faire/help-center-self-serve/chat/hooks/useChatAuthorization";
import { isLocaleChatSupported } from "@faire/help-center-self-serve/utils/isLocaleChatSupported";
import { browserLevelLocaleKey } from "@faire/help-center-utils/serialized-data/getLocale";
import { getSettingChatSLA } from "@faire/help-center-utils/settings/getSettingChatSLA";
import { ChatBubble } from "@faire/web--source/slate/icons/foundation/ChatBubble";
import { LoadingSpinner } from "@faire/web--source/slate/LoadingSpinner";
import { Spacer } from "@faire/web--source/slate/spacing";
import { responsiveMediaQueries } from "@faire/web--source/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web--source/slate/Typography";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import React from "react";
import styled from "styled-components";

import { ContactUsOptionsWrapper } from "../../components/ContactUsOptionsWrapper";
import { ColumnWrapper, LeftMarginWrapper } from "../../components/EmailUs";
import { IconWrapper } from "../../components/IconWrapper";
import { OperatingTime } from "../../components/OperatingTime";

import { FormattedAvailableTime } from "./FormattedAvailableTime";
import { useFormatTime } from "./hooks/useFormatTime";

export interface IChatProps {
  onClick?: () => void;
  disableChat?: boolean;
  loading?: boolean;
}

const ChatBtnCopy: React.FC<{ textColor: string }> = ({ textColor }) => {
  if (isLocaleChatSupported(browserLevelLocaleKey())) {
    return (
      <Typography variant="paragraphSansMedium" color={textColor}>
        <StrictLocalMsg
          defaultMessage="Chat live"
          description={{
            text: "Title of the chat button",
            img: "https://cdn.faire.com/fastly/2173a390d3f77b92c2d77cf8cabd67151689898445c0e17c59de450307405bf4.png",
          }}
        />
      </Typography>
    );
  }
  return (
    <Typography variant="paragraphSansMedium" color={textColor}>
      <StrictLocalMsg
        defaultMessage="Live chat in English"
        description={{
          text: 'Describe the chat language in "English"',
          img: "https://cdn.faire.com/fastly/2785ef8af905b8cd29c7c27f95ccc4684a396f4bec820c4d623f687391af5a58.png",
        }}
      />
    </Typography>
  );
};

export const DesktopChat: React.FC<IChatProps> = ({
  onClick,
  disableChat = false,
  loading = false,
}) => {
  const { chatBtnEnabled } = useChatAuthorization();
  const textColor =
    chatBtnEnabled && !disableChat ? Core.text.default : Core.text.subdued;
  const waitTime = getSettingChatSLA();
  const formattedWaitTime = useFormatTime(waitTime);

  if (disableChat) {
    return (
      <ContactUsOptionsWrapper
        onClick={onClick}
        disabled
        dataTestId="chat-disable-btn"
      >
        <ColumnWrapper>
          <IconWrapper disabled>
            <ChatBubble fontSize="24" />
          </IconWrapper>
          <Spacer height="1x" />
          <Spacer height="1/2x" />
          <LeftMarginWrapper>
            <ChatBtnCopy textColor={textColor} />
            <Spacer height="1x" />
            <Typography variant="labelSansRegular" color={textColor}>
              <StrictLocalMsg
                defaultMessage="Live chat is currently unavailable due to site maintenance."
                description={{
                  img: "https://cdn.faire.com/fastly/2173a390d3f77b92c2d77cf8cabd67151689898445c0e17c59de450307405bf4.png",
                }}
              />
            </Typography>
          </LeftMarginWrapper>
        </ColumnWrapper>
      </ContactUsOptionsWrapper>
    );
  }

  if (loading) {
    return (
      <LoadingWrapper>
        <LoadingSpinner />
      </LoadingWrapper>
    );
  }

  return (
    <ContactUsOptionsWrapper
      onClick={onClick}
      disabled={!chatBtnEnabled || loading}
      dataTestId="chat-btn"
    >
      <ColumnWrapper>
        <IconWrapper disabled={!chatBtnEnabled}>
          <ChatBubble fontSize="24" />
        </IconWrapper>
        <Spacer height="1x" />
        <Spacer height="1/2x" />
        <LeftMarginWrapper>
          <ChatBtnCopy textColor={textColor} />
          <Spacer height="1x" />
          {chatBtnEnabled ? (
            <Typography color={textColor}>
              <StrictLocalMsg
                defaultMessage="Average wait: {formattedWaitTime}"
                description={{
                  text: "describe the chat response time in 3 minutes",
                  img: "https://cdn.faire.com/fastly/122dfe012201ea7e1c608e760930ad677ecb01bca7df914bf5f732fc98ccf690.png",
                }}
                values={{ formattedWaitTime }}
              />
            </Typography>
          ) : (
            <>
              <OperatingTime color={textColor} supportType="chat" />
              <FormattedAvailableTime supportType="chat" color={textColor} />
            </>
          )}
        </LeftMarginWrapper>
      </ColumnWrapper>
    </ContactUsOptionsWrapper>
  );
};

const LoadingWrapper = styled(ContactUsOptionsWrapper)`
  justify-content: center;
  ${responsiveMediaQueries.mobile} {
    min-width: 270px;
  }

  ${responsiveMediaQueries.tablet} {
    min-width: 150px;
  }

  ${responsiveMediaQueries.desktop} {
    min-width: 150px;
  }

  ${responsiveMediaQueries.xLargeAndAbove} {
    min-width: 270px;
  }
`;
